import { CampaignRecipient, EngagementInsight } from '../types';
import { formatActivityTime } from '../../../../utils/dateUtils';
import { getInitials } from '../../../../utils/nameUtils';
import { countOfPeople } from '../../../../utils/nameUtils';

// Re-export utilities from central utils for backward compatibility
export { formatActivityTime as formatTime, getInitials };

export function typeMessage(
  text: string,
  setTypingText: (text: string) => void,
  setIsTypingComplete: (complete: boolean) => void,
  onComplete?: () => void
): () => void {
  setTypingText('');
  setIsTypingComplete(false);
  let i = 0;
  const speed = 8;
  const interval = setInterval(() => {
    if (i < text.length) {
      setTypingText(text.substring(0, i + 1));
      i++;
    } else {
      clearInterval(interval);
      setIsTypingComplete(true);
      onComplete?.();
    }
  }, speed);
  return () => clearInterval(interval);
}

export function getEngagementInsight(recipient: CampaignRecipient): EngagementInsight {
  const activity = recipient.activity;
  const opens = activity.filter(a => a.type === 'opened').length;
  const clicks = activity.filter(a => a.type === 'clicked').length;

  let score = 0;
  if (recipient.status === 'replied') score = 100;
  else if (recipient.status === 'clicked') score = 60 + Math.min(clicks * 10, 30);
  else if (recipient.status === 'opened') score = 30 + Math.min(opens * 10, 20);
  else if (recipient.status === 'sent' || recipient.status === 'delivered') score = 10;

  let pattern = '';
  if (opens > 2 && clicks === 0) pattern = 'Curious';
  else if (clicks >= 3) pattern = 'High intent';
  else if (opens > 1 && clicks > 0) pattern = 'Engaged';
  else if (recipient.status === 'replied') pattern = 'Converted';

  return { score, pattern, opens, clicks };
}

export function generateCampaignSummary(
  campaignName: string,
  stats: { sent: number; opened: number },
  recipients: CampaignRecipient[]
): string {
  const openRate = stats.sent > 0 ? Math.round((stats.opened / stats.sent) * 100) : 0;
  const repliedLeads = recipients.filter(r => r.status === 'replied');
  const highIntentLeads = recipients.filter(r => r.status === 'clicked' && r.clickCount >= 3);
  const engagedLeads = recipients.filter(r => r.status === 'clicked' && r.clickCount >= 1 && r.clickCount < 3);

  let summary = `Hey! I've been looking at **${campaignName}** and here's what I found:\n\n`;

  summary += `You've reached out to **${countOfPeople(stats.sent)}**`;
  if (openRate > 0) {
    summary += ` and ${openRate}% opened your email`;
    if (openRate > 30) summary += " — that's really solid!";
    else if (openRate > 20) summary += " — nice work!";
  }
  summary += '\n\n';

  if (repliedLeads.length > 0) {
    summary += `Great news: **${repliedLeads[0].name}** replied! `;
    if (repliedLeads.length > 1) {
      summary += `(Plus ${repliedLeads.length - 1} ${repliedLeads.length - 1 === 1 ? 'other' : 'others'}) `;
    }
    summary += `This is a warm conversation waiting to happen.\n\n`;
  }

  if (highIntentLeads.length > 0) {
    summary += `I noticed **${highIntentLeads.length} ${highIntentLeads.length === 1 ? 'lead is' : 'leads are'}** clicking multiple times — they're clearly interested. Might be worth reaching out while the momentum is there.\n\n`;
  }

  if (engagedLeads.length > 0 && repliedLeads.length === 0 && highIntentLeads.length === 0) {
    summary += `**${countOfPeople(engagedLeads.length)}** clicked through — good sign of interest!\n\n`;
  }

  if (repliedLeads.length === 0 && highIntentLeads.length === 0 && engagedLeads.length === 0 && openRate < 20) {
    summary += `Still early days — campaigns often take a few touches to gain traction. Want me to suggest ways to boost engagement?\n\n`;
  }

  summary += `How can I help?`;

  return summary;
}

interface ChatCampaignContext {
  name: string;
  status: string;
  stats: { sent: number; opened: number; clicked: number; replied: number; queued: number; bounced: number; draft: number };
  recipients: CampaignRecipient[];
}

export function generateChatResponse(userMessage: string, campaign?: ChatCampaignContext): string {
  const input = userMessage.toLowerCase();
  const stats = campaign?.stats;
  const recipients = campaign?.recipients || [];

  if (input.includes('follow') || input.includes('draft')) {
    const nameMatch = userMessage.match(/for\s+(\w+(?:\s+\w+)?)/i);
    const name = nameMatch ? nameMatch[1] : 'this lead';
    return `Here's a follow-up for ${name}:\n\n**Subject:** Quick follow-up\n\nHi ${name.split(' ')[0]},\n\nI wanted to follow up on my previous email. Would you have 15 minutes this week for a quick call?\n\nBest regards`;
  }

  if (input.includes('help') || input.includes('what can')) {
    return `I can help you with:\n\n• **Draft follow-ups** — "Draft a follow-up for Sarah"\n• **Campaign analysis** — "Who's most engaged?"\n• **Open rate tips** — "How can I improve open rates?"\n• **Subject lines** — "Suggest a better subject line"\n• **Performance** — "How is this campaign performing?"`;
  }

  if ((input.includes('who') && input.includes('engag')) || input.includes('most engaged') || input.includes('interested')) {
    const clicked = recipients.filter(r => r.status === 'clicked');
    const replied = recipients.filter(r => r.status === 'replied');
    const highIntent = recipients.filter(r => r.clickCount >= 3);

    let response = '';
    if (replied.length > 0) {
      const names = replied.slice(0, 3).map(r => `**${r.name}**`).join(', ');
      response += `Replies (highest engagement): ${names}${replied.length > 3 ? ` and ${replied.length - 3} more` : ''}\n\n`;
    }
    if (highIntent.length > 0) {
      const names = highIntent.slice(0, 3).map(r => `**${r.name}**`).join(', ');
      response += `High intent (3+ clicks): ${names}${highIntent.length > 3 ? ` and ${highIntent.length - 3} more` : ''}\n\n`;
    }
    if (clicked.length > 0 && highIntent.length === 0) {
      const names = clicked.slice(0, 3).map(r => `**${r.name}**`).join(', ');
      response += `Clicked: ${names}${clicked.length > 3 ? ` and ${clicked.length - 3} more` : ''}\n\n`;
    }
    if (!response) {
      response = 'No strong engagement signals yet. Campaigns often need a few touches before leads engage — consider sending a follow-up.\n\n';
    }
    response += 'Want me to draft a follow-up for any of these leads?';
    return response;
  }

  if (input.includes('open rate') || input.includes('opens') || input.includes('boost') || input.includes('improve')) {
    const openRate = stats && stats.sent > 0 ? Math.round((stats.opened / stats.sent) * 100) : 0;
    let response = '';
    if (stats && stats.sent > 0) {
      response += `Your current open rate is **${openRate}%** (${stats.opened} of ${stats.sent}).\n\n`;
    }
    response += `Here are some tips to improve opens:\n\n`;
    response += `• **Personalize subject lines** — use the recipient's first name or company\n`;
    response += `• **Keep it short** — subject lines under 50 characters perform best\n`;
    response += `• **Create curiosity** — ask a question or hint at a benefit\n`;
    response += `• **Optimize send time** — Tuesday–Thursday mornings tend to get higher opens\n`;
    response += `• **Avoid spam triggers** — skip ALL CAPS and excessive punctuation`;
    return response;
  }

  if (input.includes('subject') && (input.includes('suggest') || input.includes('idea') || input.includes('better') || input.includes('line'))) {
    const campaignName = campaign?.name || 'your campaign';
    return `Here are some subject line ideas for **${campaignName}**:\n\n• "Quick question about [their company]"\n• "[First name], thought you'd find this useful"\n• "Idea for [their company]'s [pain point]"\n• "Can I share something relevant to [their role]?"\n• "[Mutual connection] suggested I reach out"\n\nPersonalized subject lines typically get 20-30% higher open rates. Would you like me to draft one tailored to your audience?`;
  }

  if (input.includes('respond') || input.includes('reply') || input.includes('replied')) {
    const replied = recipients.filter(r => r.status === 'replied');
    if (replied.length > 0) {
      const names = replied.slice(0, 3).map(r => `**${r.name}**`).join(', ');
      return `You have ${replied.length} ${replied.length === 1 ? 'reply' : 'replies'} from: ${names}${replied.length > 3 ? ` and ${replied.length - 3} more` : ''}.\n\nTo keep the momentum going:\n• **Respond within 24 hours** — fast replies show professionalism\n• **Acknowledge their interest** — reference what they engaged with\n• **Propose a next step** — suggest a call or meeting\n\nWant me to draft a response for one of them?`;
    }
    return 'No replies yet. Consider sending a follow-up to leads who opened or clicked — they showed interest but may need another nudge.';
  }

  if (input.includes('stats') || input.includes('performance') || input.includes('how is') || input.includes('analytics') || input.includes('overview')) {
    if (!stats || stats.sent === 0) {
      return `**${campaign?.name || 'This campaign'}** hasn't sent any emails yet. Once emails are sent, I'll break down open rates, clicks, and replies here.`;
    }
    const openRate = Math.round((stats.opened / stats.sent) * 100);
    const clickRate = Math.round((stats.clicked / stats.sent) * 100);
    let response = `Here's a breakdown of **${campaign?.name || 'this campaign'}**:\n\n`;
    response += `• **Sent:** ${stats.sent}\n`;
    response += `• **Opened:** ${stats.opened} (${openRate}%)\n`;
    response += `• **Clicked:** ${stats.clicked} (${clickRate}%)\n`;
    response += `• **Replied:** ${stats.replied}\n`;
    if (stats.bounced > 0) response += `• **Bounced:** ${stats.bounced}\n`;
    response += '\n';
    if (openRate >= 30) response += 'Open rate looks strong — your subject line is working well.';
    else if (openRate >= 15) response += 'Open rate is average — try A/B testing your subject line for a lift.';
    else response += 'Open rate is below average — consider testing different subject lines or adjusting send timing.';
    return response;
  }

  if (input.includes('schedule') || input.includes('timing') || input.includes('when') || input.includes('best time')) {
    return `Here's what works for email timing:\n\n• **Best days:** Tuesday, Wednesday, Thursday\n• **Best times:** 9–11 AM in the recipient's timezone\n• **Avoid:** Monday mornings and Friday afternoons\n• **Spacing:** Wait 3–5 days between follow-ups\n\nYou can adjust your send window in the campaign settings. Want me to open settings?`;
  }

  // Default - provide helpful guidance
  return `I can help you analyze and optimize this campaign. Try asking:\n\n• "How is this campaign performing?"\n• "Who's most engaged?"\n• "How can I improve open rates?"\n• "Suggest better subject lines"\n• "Draft a follow-up for [name]"`;
}
